import React, { useEffect, useState } from 'react';
import { CheckCircle2, HandCoins, Loader2, Save } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useAuth } from '../../../src/contexts/AuthContext';

/**
 * Configuração do acordo de parcelamento com juros. Chamado #5686.
 *
 * Card autocontido: carrega e salva as próprias chaves. O ApisView mantém um
 * bloco de estado por integração (url + saving + success + loader + handler), e
 * replicar esse padrão para quatro chaves novas somaria ~120 linhas de
 * boilerplate num arquivo que já passa de 2.200.
 */

const CHAVES = [
    'parcelamento_termo_webhook_url',
    'parcelamento_prazo_validade_dias',
    'parcelamento_taxa_juros_minima',
    'parcelamento_desconto_saldo_maximo',
] as const;

type Chave = (typeof CHAVES)[number];

const ParcelamentoConfigCard: React.FC = () => {
    const { user } = useAuth();
    const [valores, setValores] = useState<Record<Chave, string>>({
        parcelamento_termo_webhook_url: '',
        parcelamento_prazo_validade_dias: '7',
        parcelamento_taxa_juros_minima: '0',
        parcelamento_desconto_saldo_maximo: '100',
    });
    const [salvando, setSalvando] = useState(false);
    const [salvo, setSalvo] = useState(false);

    useEffect(() => {
        (async () => {
            const { data } = await supabase
                .from('system_settings')
                .select('key, value')
                .in('key', CHAVES as unknown as string[]);
            if (!data) return;
            setValores((prev) => {
                const novo = { ...prev };
                data.forEach((r: any) => {
                    if (r.value !== null && r.value !== undefined) novo[r.key as Chave] = String(r.value);
                });
                return novo;
            });
        })();
    }, []);

    const set = (k: Chave, v: string) => setValores((prev) => ({ ...prev, [k]: v }));

    const salvar = async () => {
        setSalvando(true);
        setSalvo(false);
        try {
            const linhas = CHAVES.map((k) => ({ key: k, value: valores[k], updated_at: new Date() }));
            const { error } = await supabase.from('system_settings').upsert(linhas);
            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSalvo(true);
            setTimeout(() => setSalvo(false), 3000);
        } catch (err) {
            console.error('Erro ao salvar configuração de parcelamento:', err);
            alert('Erro ao salvar a configuração de parcelamento.');
        } finally {
            setSalvando(false);
        }
    };

    const campo = 'w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm';
    const rotulo = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg">
                    <HandCoins size={20} />
                </div>
                <div>
                    <h3 className="font-bold text-gray-800 dark:text-white">Acordos de Parcelamento</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Termo de Confissão de Dívida, prazo de validade da proposta e alçada de aprovação.
                    </p>
                </div>
            </div>

            <div className="p-6 space-y-4">
                <div>
                    <label className={rotulo}>URL do Webhook do Termo (n8n)</label>
                    <input
                        type="url"
                        value={valores.parcelamento_termo_webhook_url}
                        onChange={(e) => set('parcelamento_termo_webhook_url', e.target.value)}
                        placeholder="https://n8n.seuservidor.com/webhook/vigorhub-termo-parcelamento"
                        className={campo}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Gera o Termo de Confissão de Dívida no Google Docs e cria o documento no Autentique.
                        Precisa responder <code className="font-mono">{'{ ok: true, id_autentique, link_assinatura_cliente, numero_termo }'}</code> —
                        sem esse <code className="font-mono">ok</code> o sistema recusa, porque o n8n devolve 200 com
                        corpo vazio quando o fluxo morre antes de responder.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className={rotulo}>Validade da proposta (dias)</label>
                        <input
                            type="number" min={1}
                            value={valores.parcelamento_prazo_validade_dias}
                            onChange={(e) => set('parcelamento_prazo_validade_dias', e.target.value)}
                            className={campo}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Sem assinatura e entrada paga até lá, a proposta expira e o boleto da entrada é cancelado.
                        </p>
                    </div>

                    <div>
                        <label className={rotulo}>Juros mínimos (% a.m.)</label>
                        <input
                            type="number" step="0.01" min={0}
                            value={valores.parcelamento_taxa_juros_minima}
                            onChange={(e) => set('parcelamento_taxa_juros_minima', e.target.value)}
                            className={campo}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Abaixo disso o acordo nasce travado esperando aprovação. <strong>0 desliga a trava.</strong>
                        </p>
                    </div>

                    <div>
                        <label className={rotulo}>Desconto máximo no saldo (%)</label>
                        <input
                            type="number" step="0.1" min={0} max={100}
                            value={valores.parcelamento_desconto_saldo_maximo}
                            onChange={(e) => set('parcelamento_desconto_saldo_maximo', e.target.value)}
                            className={campo}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Acima disso o acordo exige aprovação. <strong>100 desliga a trava.</strong>
                        </p>
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        onClick={salvar}
                        disabled={salvando}
                        className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all ${salvo ? 'bg-warning text-white hover:bg-warning/90' : 'bg-primary text-white hover:bg-primary/90'}`}
                    >
                        {salvando ? <Loader2 size={16} className="animate-spin" />
                            : salvo ? <><CheckCircle2 size={16} /> Salvo!</>
                                : <><Save size={16} /> Salvar</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ParcelamentoConfigCard;
